/**
 * Canonical Item Relationships Store
 *
 * Phase 1:
 * - additive runtime support for canonical relationship tables
 * - legacy cluster store remains in place for existing UI paths
 * - new relationship work should prefer this store
 */

import { create } from 'zustand';
import {
  createInitialSyncState,
  deleteRecord,
  generateUUID,
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  insertRecord,
  isSupabaseAvailable,
  type SyncState,
  updateRecord,
} from '../../lib/supabaseSync';
import {
  containsEdgeValidationMessage,
  validateContainsEdge,
} from '../../lib/containmentHierarchy';
import { carriesMagnitudeProblem } from '../../lib/carriesRelationships';
import { DEFAULT_SEQUENCE_STRENGTH, isSequenceEdgeType } from '../../lib/sequenceRelationships';
import {
  logRelationshipEdgeCreated,
  logRelationshipEdgeDissolved,
} from '../../lib/relationshipAuditEvents';
import {
  itemClosureEventRowToDomain,
  itemMutationReviewRowToDomain,
  itemRelationshipEdgeRowToDomain,
  itemRelationshipGroupMembershipRowToDomain,
  itemRelationshipGroupRowToDomain,
} from '../../types/prioritization/itemRelationships';
import type {
  ItemClosureEvent,
  ItemClosureEventRow,
  ItemMutationReview,
  ItemMutationReviewRow,
  ItemRelationshipEdge,
  ItemRelationshipEdgeRow,
  ItemRelationshipGroup,
  ItemRelationshipGroupMembership,
  ItemRelationshipGroupMembershipRow,
  ItemRelationshipGroupRow,
} from '../../types/prioritization/itemRelationships';
import type {
  LineageEvent,
  LineageEventRow,
  LineageRelationshipResolution,
  LineageRelationshipResolutionRow,
} from '../../types/prioritization/itemLineage';
import {
  lineageEventRowToDomain,
  lineageRelationshipResolutionRowToDomain,
} from '../../types/prioritization/itemLineage';
import { useItemsStore, recordItemFieldChange } from './itemsStore';
import { planClusterResolution, type ClusterResolutionOutcome } from '../../lib/clusterResolution';
import { resolveItemCluster } from '../../lib/governedTransactions';

const GROUPS_TABLE = 'item_relationship_groups';
const GROUP_MEMBERSHIPS_TABLE = 'item_relationship_group_memberships';
const EDGES_TABLE = 'item_relationship_edges';
const MUTATION_REVIEWS_TABLE = 'item_mutation_reviews';
const CLOSURE_EVENTS_TABLE = 'item_closure_events';
const LINEAGE_RELATIONSHIP_RESOLUTIONS_TABLE = 'lineage_relationship_resolutions';
const LINEAGE_EVENTS_TABLE = 'lineage_events';

async function fetchWorkspaceRows<T>(tableName: string, orderColumn: string): Promise<T[]> {
  const supabase = getSafeSupabaseClient();
  const workspaceId = getCurrentWorkspaceId();
  if (!supabase || !workspaceId) return [];

  const { data, error } = await supabase
    .from(tableName)
    .select('*')
    .eq('workspace_id', workspaceId)
    .order(orderColumn, { ascending: false });

  if (error) {
    console.error(`[ItemRelationshipsStore] Failed to fetch ${tableName}:`, error.message);
    return [];
  }

  return (data as T[] | null) ?? [];
}

/**
 * Fetch lineage events, scoped to the current workspace.
 *
 * lineage_events has no RLS policy so we apply the workspace filter at the
 * query level using a PostgREST `or` clause. This keeps behaviour consistent
 * with fetchWorkspaceRows (returns [] when workspace is unknown) and avoids
 * full-table scans that previously caused a flash of unscoped data.
 *
 * Legacy events written before migration 223 may have workspace_id = null;
 * the `.is.null` arm of the or-clause keeps those visible.
 */
async function fetchLineageEventRows(): Promise<LineageEventRow[]> {
  const supabase = getSafeSupabaseClient();
  const workspaceId = getCurrentWorkspaceId();

  // Consistent with fetchWorkspaceRows — never return cross-workspace data.
  if (!supabase || !workspaceId) return [];

  const { data, error } = await supabase
    .from(LINEAGE_EVENTS_TABLE)
    .select('*')
    .or(`workspace_id.eq.${workspaceId},workspace_id.is.null`)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[ItemRelationshipsStore] Failed to fetch lineage_events:', error.message);
    return [];
  }

  return (data as LineageEventRow[] | null) ?? [];
}

interface ItemRelationshipsState {
  groups: ItemRelationshipGroup[];
  groupMemberships: ItemRelationshipGroupMembership[];
  edges: ItemRelationshipEdge[];
  mutationReviews: ItemMutationReview[];
  closureEvents: ItemClosureEvent[];
  lineageEvents: LineageEvent[];
  lineageRelationshipResolutions: LineageRelationshipResolution[];
  syncState: SyncState;

  fetchFromSupabase: () => Promise<void>;

  createGroup: (input: {
    type: ItemRelationshipGroup['type'];
    subtype?: ItemRelationshipGroup['subtype'];
    status?: ItemRelationshipGroup['status'];
    declaredMode?: ItemRelationshipGroup['declaredMode'];
    title?: string;
    note?: string;
    rationale?: string;
    createdBy?: string;
    memberItemIds?: string[];
    canonicalItemId?: string;
  }) => Promise<ItemRelationshipGroup | null>;
  updateGroup: (groupId: string, updates: Partial<ItemRelationshipGroup>) => Promise<boolean>;
  dissolveGroup: (groupId: string) => Promise<boolean>;
  /**
   * Doc 12 Rule 1 — the explicit cluster resolution. duplicate / reframe close
   * the non-canonical members (closure record; lineage only for duplicate);
   * variant keeps all. The group becomes `confirmed` with its decided subtype
   * and a reviewer stamp. Plan is computed by src/lib/clusterResolution.ts.
   */
  resolveCluster: (
    groupId: string,
    input: { outcome: ClusterResolutionOutcome; canonicalItemId?: string; rationale: string; actorId: string },
  ) => Promise<{ success: boolean; error?: string; canonicalItemId?: string }>;
  addGroupMembers: (
    groupId: string,
    itemIds: string[],
    options?: { canonicalItemId?: string; inclusionReason?: string; addedBy?: string },
  ) => Promise<boolean>;
  removeGroupMember: (groupId: string, itemId: string) => Promise<boolean>;

  createEdge: (input: Omit<ItemRelationshipEdge, 'id' | 'createdAt'>) => Promise<ItemRelationshipEdge | null>;
  updateEdge: (edgeId: string, updates: Partial<ItemRelationshipEdge>) => Promise<boolean>;
  dissolveEdge: (edgeId: string) => Promise<boolean>;

  createMutationReview: (input: Omit<ItemMutationReview, 'id' | 'requestedAt'>) => Promise<ItemMutationReview | null>;
  createClosureEvent: (input: Omit<ItemClosureEvent, 'id' | 'createdAt'>) => Promise<ItemClosureEvent | null>;
  createLineageRelationshipResolution: (
    input: Omit<LineageRelationshipResolution, 'id' | 'resolvedAt'>,
  ) => Promise<LineageRelationshipResolution | null>;

  getGroupById: (groupId: string) => ItemRelationshipGroup | undefined;
  getGroupsForItem: (itemId: string) => ItemRelationshipGroup[];
  getEdgesForItem: (itemId: string) => ItemRelationshipEdge[];
  getMutationReviewsForItem: (itemId: string) => ItemMutationReview[];
  getClosureEventsForItem: (itemId: string) => ItemClosureEvent[];
}

export const useItemRelationshipsStore = create<ItemRelationshipsState>()((set, get) => ({
  groups: [],
  groupMemberships: [],
  edges: [],
  mutationReviews: [],
  closureEvents: [],
  lineageEvents: [],
  lineageRelationshipResolutions: [],
  syncState: createInitialSyncState(),

  fetchFromSupabase: async () => {
    if (!isSupabaseAvailable()) return;

    set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });

    try {
      const [
        groupRows,
        membershipRows,
        edgeRows,
        mutationReviewRows,
        closureEventRows,
        lineageEventRows,
        lineageResolutionRows,
      ] = await Promise.all([
        fetchWorkspaceRows<ItemRelationshipGroupRow>(GROUPS_TABLE, 'created_at'),
        fetchWorkspaceRows<ItemRelationshipGroupMembershipRow>(GROUP_MEMBERSHIPS_TABLE, 'added_at'),
        fetchWorkspaceRows<ItemRelationshipEdgeRow>(EDGES_TABLE, 'created_at'),
        fetchWorkspaceRows<ItemMutationReviewRow>(MUTATION_REVIEWS_TABLE, 'requested_at'),
        fetchWorkspaceRows<ItemClosureEventRow>(CLOSURE_EVENTS_TABLE, 'created_at'),
        fetchLineageEventRows(),
        fetchWorkspaceRows<LineageRelationshipResolutionRow>(LINEAGE_RELATIONSHIP_RESOLUTIONS_TABLE, 'resolved_at'),
      ]);

      set({
        groups: groupRows.map(itemRelationshipGroupRowToDomain),
        groupMemberships: membershipRows.map(itemRelationshipGroupMembershipRowToDomain),
        edges: edgeRows.map(itemRelationshipEdgeRowToDomain),
        mutationReviews: mutationReviewRows.map(itemMutationReviewRowToDomain),
        closureEvents: closureEventRows.map(itemClosureEventRowToDomain),
        lineageEvents: lineageEventRows.map(lineageEventRowToDomain),
        lineageRelationshipResolutions: lineageResolutionRows.map(lineageRelationshipResolutionRowToDomain),
        syncState: {
          ...get().syncState,
          isLoading: false,
          lastSyncAt: new Date(),
          syncError: null,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      set({
        syncState: {
          ...get().syncState,
          isLoading: false,
          syncError: message,
        },
      });
      console.error('[ItemRelationshipsStore] fetchFromSupabase failed:', error);
    }
  },

  createGroup: async (input) => {
    const workspaceId = getCurrentWorkspaceId();
    const group: ItemRelationshipGroup = {
      id: generateUUID(),
      type: input.type,
      subtype: input.subtype,
      status: input.status ?? 'candidate',
      declaredMode: input.declaredMode ?? 'declared',
      title: input.title,
      note: input.note,
      rationale: input.rationale,
      createdAt: new Date(),
      createdBy: input.createdBy ?? 'unknown',
      workspaceId: workspaceId ?? undefined,
    };

    const membershipRecords: ItemRelationshipGroupMembership[] = (input.memberItemIds ?? []).map((itemId) => ({
      id: generateUUID(),
      groupId: group.id,
      itemId,
      membershipRole: input.canonicalItemId === itemId ? 'canonical' : 'member',
      addedAt: new Date(),
      addedBy: input.createdBy,
      inclusionReason: input.rationale,
      workspaceId: workspaceId ?? undefined,
    }));

    set((state) => ({
      groups: [...state.groups, group],
      groupMemberships: [...state.groupMemberships, ...membershipRecords],
    }));

    const groupResult = await insertRecord(GROUPS_TABLE, group);
    if (groupResult.error) {
      console.error('[ItemRelationshipsStore] Failed to create group:', groupResult.error);
      set((state) => ({
        groups: state.groups.filter((g) => g.id !== group.id),
        groupMemberships: state.groupMemberships.filter(
          (m) => !membershipRecords.some((mr) => mr.id === m.id),
        ),
      }));
      return null;
    }

    for (const membership of membershipRecords) {
      const membershipResult = await insertRecord(GROUP_MEMBERSHIPS_TABLE, membership);
      if (membershipResult.error) {
        console.error('[ItemRelationshipsStore] Failed to create group membership:', membershipResult.error);
        set((state) => ({
          groupMemberships: state.groupMemberships.filter((m) => m.id !== membership.id),
        }));
        continue;
      }
      // Each member's history says which group it joined (2026-10-15; this left no trace).
      recordItemFieldChange(membership.itemId, 'relationshipGroup', null, `${group.type}${group.title ? ` · ${group.title}` : ''}`);
    }

    return group;
  },

  updateGroup: async (groupId, updates) => {
    const existing = get().groups.find((group) => group.id === groupId);
    if (!existing) return false;

    const updated: ItemRelationshipGroup = {
      ...existing,
      ...updates,
    };

    set((state) => ({
      groups: state.groups.map((group) => (group.id === groupId ? updated : group)),
    }));

    const result = await updateRecord(GROUPS_TABLE, groupId, updated);
    if (result.error) {
      console.error('[ItemRelationshipsStore] Failed to update group:', result.error);
      return false;
    }

    // Log a history event on each member item when the cluster title changes
    if (updates.title !== undefined && updates.title !== existing.title) {
      const memberItemIds = get()
        .groupMemberships.filter((m) => m.groupId === groupId)
        .map((m) => m.itemId);
      for (const itemId of memberItemIds) {
        // `type`, not `eventType`: the audit row's event type comes from `type`; this
        // was stored with an empty one (2026-10-15).
        recordItemFieldChange(itemId, 'relationshipGroupName', existing.title ?? '', updates.title);
      }
    }

    return true;
  },

  dissolveGroup: async (groupId) => get().updateGroup(groupId, { status: 'dissolved' }),

  resolveCluster: async (groupId, input) => {
    const group = get().groups.find((g) => g.id === groupId);
    if (!group) return { success: false, error: 'Cluster not found' };
    const rationale = input.rationale.trim();
    if (!rationale) return { success: false, error: 'A rationale is required to resolve a cluster.' };

    // The pure plan is the early answer for the dialog (and the shape the
    // server applies); the server re-derives membership and decides.
    const planned = planClusterResolution({
      group,
      memberships: get().groupMemberships,
      outcome: input.outcome,
      canonicalItemId: input.canonicalItemId,
    });
    if (!planned.ok) return { success: false, error: planned.reason };
    const { plan } = planned;

    // One governed transaction (resolve_item_cluster, migration 749): closes
    // the non-kept members (lineage only for duplicate — Rule 2), writes the
    // closure records and audit trail, confirms the group and records the
    // Rule 1 Decision — all together, or nothing. "Items were closed but the
    // cluster could not be marked resolved" can no longer happen.
    const result = await resolveItemCluster(groupId, plan.outcome, plan.canonicalItemId, rationale);
    if (!result.ok) return { success: false, error: result.error ?? 'The cluster could not be resolved. Nothing was changed.' };

    const now = new Date();
    const closedIds = Array.isArray(result.data?.closed_item_ids)
      ? (result.data.closed_item_ids as unknown[]).filter((id): id is string => typeof id === 'string')
      : plan.closures.map((c) => c.itemId);
    const keptIds = Array.isArray(result.data?.kept_item_ids)
      ? (result.data.kept_item_ids as unknown[]).filter((id): id is string => typeof id === 'string')
      : plan.survivorItemIds;
    const canonicalId = typeof result.data?.canonical_item_id === 'string' ? result.data.canonical_item_id : plan.canonicalItemId;

    // Mirror what the server wrote — local state only, no second write.
    useItemsStore.setState((state) => ({
      items: state.items.map((item) => {
        if (closedIds.includes(item.id)) {
          return { ...item, exit_status: 'archived', duplicateOfId: canonicalId, dedupeStatus: 'duplicate_of', updatedAt: now } as typeof item;
        }
        if (keptIds.includes(item.id)) return { ...item, dedupeStatus: 'unique', updatedAt: now } as typeof item;
        return item;
      }),
    }));
    set((state) => ({
      groups: state.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              status: plan.groupUpdate.status,
              subtype: plan.groupUpdate.subtype,
              reviewedAt: now,
              reviewedBy: input.actorId,
              rationale: g.rationale ? `${g.rationale}\n\nResolved (${plan.outcome}): ${rationale}` : `Resolved (${plan.outcome}): ${rationale}`,
            }
          : g,
      ),
    }));

    return { success: true, canonicalItemId: canonicalId };
  },

  addGroupMembers: async (groupId, itemIds, options) => {
    const workspaceId = getCurrentWorkspaceId();
    const memberships = itemIds.map((itemId) => ({
      id: generateUUID(),
      groupId,
      itemId,
      membershipRole: options?.canonicalItemId === itemId ? 'canonical' : 'member',
      addedAt: new Date(),
      addedBy: options?.addedBy,
      inclusionReason: options?.inclusionReason,
      workspaceId: workspaceId ?? undefined,
    }));

    set((state) => ({
      groupMemberships: [...state.groupMemberships, ...memberships],
    }));

    for (const membership of memberships) {
      const result = await insertRecord(GROUP_MEMBERSHIPS_TABLE, membership);
      if (result.error) {
        console.error('[ItemRelationshipsStore] Failed to add group member:', result.error);
        set((state) => ({
          groupMemberships: state.groupMemberships.filter(
            (m) => !memberships.some((mb) => mb.id === m.id),
          ),
        }));
        return false;
      }
      const g = get().groups.find((x) => x.id === groupId);
      recordItemFieldChange(membership.itemId, 'relationshipGroup', null, g ? `${g.type}${g.title ? ` · ${g.title}` : ''}` : groupId);
    }

    return true;
  },

  removeGroupMember: async (groupId, itemId) => {
    const membership = get().groupMemberships.find((entry) => entry.groupId === groupId && entry.itemId === itemId);
    if (!membership) return false;

    set((state) => ({
      groupMemberships: state.groupMemberships.filter((entry) => entry.id !== membership.id),
    }));

    const result = await deleteRecord(GROUP_MEMBERSHIPS_TABLE, membership.id);
    if (result.error || result.data !== true) {
      console.error('[ItemRelationshipsStore] Failed to remove group member:', result.error ?? 'Unknown error');
      set((state) => ({
        groupMemberships: [...state.groupMemberships, membership],
      }));
      return false;
    }

    const g = get().groups.find((x) => x.id === groupId);
    recordItemFieldChange(itemId, 'relationshipGroup', g ? `${g.type}${g.title ? ` · ${g.title}` : ''}` : groupId, null);
    return true;
  },

  createEdge: async (input) => {
    // Carries states its share; no other type has one (canon 12 §5.11, migration 968).
    const magnitudeProblem = carriesMagnitudeProblem(input.type, input.magnitude);
    if (magnitudeProblem) {
      console.error('[ItemRelationshipsStore] Edge rejected:', magnitudeProblem);
      set({ syncError: magnitudeProblem } as Partial<ItemRelationshipsState>);
      return null;
    }

    if (input.type === 'contains') {
      const violation = validateContainsEdge(
        input.sourceItemId,
        input.targetItemId,
        get().edges,
      );
      if (violation) {
        console.error(
          '[ItemRelationshipsStore] Contains edge rejected:',
          containsEdgeValidationMessage(violation),
        );
        return null;
      }
    }

    // A dependency or prerequisite edge states its strength (canon 12 §5.4;
    // migration 1004 refuses one without). A writer with no strength to state —
    // an agent's proposal, a connection drawn on the graph — gets the floor,
    // soft: related in sequence, binding nothing. A stated value is kept as is;
    // one outside the four is refused by the database, loudly.
    const subtype =
      isSequenceEdgeType(input.type) && input.subtype == null ? DEFAULT_SEQUENCE_STRENGTH : input.subtype;

    const edge: ItemRelationshipEdge = {
      ...input,
      subtype,
      id: generateUUID(),
      createdAt: new Date(),
    };

    set((state) => ({
      edges: [...state.edges, edge],
    }));

    const result = await insertRecord(EDGES_TABLE, edge);
    if (result.error) {
      console.error('[ItemRelationshipsStore] Failed to create edge:', result.error);
      set((state) => ({
        edges: state.edges.filter((e) => e.id !== edge.id),
        syncError: result.error ?? 'Failed to create relationship edge',
      }));
      return null;
    }

    set({ syncError: null });
    logRelationshipEdgeCreated(edge);
    return edge;
  },

  updateEdge: async (edgeId, updates) => {
    const existing = get().edges.find((edge) => edge.id === edgeId);
    if (!existing) return false;

    const updated = { ...existing, ...updates };
    set((state) => ({
      edges: state.edges.map((edge) => (edge.id === edgeId ? updated : edge)),
    }));

    const result = await updateRecord(EDGES_TABLE, edgeId, updated);
    if (result.error) {
      console.error('[ItemRelationshipsStore] Failed to update edge:', result.error);
      return false;
    }

    if (updates.status === 'dissolved' && existing.status !== 'dissolved') {
      logRelationshipEdgeDissolved(updated);
    }

    return true;
  },

  dissolveEdge: async (edgeId) => get().updateEdge(edgeId, { status: 'dissolved' }),

  createMutationReview: async (input) => {
    const review: ItemMutationReview = {
      ...input,
      id: generateUUID(),
      requestedAt: new Date(),
    };

    set((state) => ({
      mutationReviews: [...state.mutationReviews, review],
    }));

    const result = await insertRecord(MUTATION_REVIEWS_TABLE, review);
    if (result.error) {
      console.error('[ItemRelationshipsStore] Failed to create mutation review:', result.error);
      return null;
    }

    return review;
  },

  createClosureEvent: async (input) => {
    const closureEvent: ItemClosureEvent = {
      ...input,
      id: generateUUID(),
      createdAt: new Date(),
    };

    set((state) => ({
      closureEvents: [...state.closureEvents, closureEvent],
    }));

    const result = await insertRecord(CLOSURE_EVENTS_TABLE, closureEvent);
    if (result.error) {
      console.error('[ItemRelationshipsStore] Failed to create closure event:', result.error);
      return null;
    }

    return closureEvent;
  },

  createLineageRelationshipResolution: async (input) => {
    const resolution: LineageRelationshipResolution = {
      ...input,
      id: generateUUID(),
      resolvedAt: new Date(),
    };

    set((state) => ({
      lineageRelationshipResolutions: [...state.lineageRelationshipResolutions, resolution],
    }));

    const result = await insertRecord(LINEAGE_RELATIONSHIP_RESOLUTIONS_TABLE, resolution);
    if (result.error) {
      console.error('[ItemRelationshipsStore] Failed to create lineage relationship resolution:', result.error);
      return null;
    }

    return resolution;
  },

  getGroupById: (groupId) => get().groups.find((group) => group.id === groupId),

  getGroupsForItem: (itemId) => {
    const memberships = get().groupMemberships.filter((membership) => membership.itemId === itemId);
    const groupIds = new Set(memberships.map((membership) => membership.groupId));
    return get().groups.filter((group) => groupIds.has(group.id));
  },

  getEdgesForItem: (itemId) =>
    get().edges.filter((edge) => edge.sourceItemId === itemId || edge.targetItemId === itemId),

  getMutationReviewsForItem: (itemId) =>
    get().mutationReviews.filter((review) => review.itemId === itemId),

  getClosureEventsForItem: (itemId) =>
    get().closureEvents.filter((event) => event.itemId === itemId),
}));
